import BottomSheet from '@components/BottomSheet'
import Touchable from '@components/Touchable'
import { Address } from '@modules/Home/Components/Addresses'
import { UserActivity } from '@modules/UserActivity'
import { useContacts } from '@plug-core/state'
import { addressToChain } from '@plug-core/utils/addressMatcher'
import { Avatar, Button, Field, Heading, Input, Sheet } from '@ui'
import EmojiPicker, { Theme } from 'emoji-picker-react'
import React, { CSSProperties, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MdDelete } from 'react-icons/md'
import { useLocation, useNavigate } from 'react-router-dom'
import { AddressInput } from './components/AddressInput'

/** Points the emoji picker's dark theme at Plug tokens so it follows the active tone. */
const EMOJI_PICKER_STYLE = {
  '--epr-dark-bg-color': 'transparent',
  '--epr-dark-category-label-bg-color': 'var(--plug-sheet)',
  '--epr-dark-picker-border-color': 'transparent',
  '--epr-dark-search-input-bg-color': 'var(--plug-field)',
  '--epr-dark-search-input-bg-color-active': 'var(--plug-field)',
  '--epr-dark-hover-bg-color': 'var(--plug-surface-2)',
  '--epr-dark-focus-bg-color': 'var(--plug-surface-2)',
  '--epr-dark-text-color': 'var(--plug-text)',
  '--epr-dark-highlight-color': 'var(--plug-accent)',
  '--epr-dark-category-icon-active-color': 'var(--plug-accent)',
} as CSSProperties

const ContactDetails = () => {
  const { updateContact, getContactById } = useContacts()
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const [emojisPickerVisible, setEmojisPickerVisible] = useState(false)
  const [addressInputVisible, setAddressInputVisible] = useState(false)

  const contact = getContactById(location.state.id)
  const initialName = useRef(contact?.name)

  useEffect(() => {
    if (!contact) navigate(-1)
  }, [contact?.id])

  useEffect(() => {
    return () => {
      const updatedContact = getContactById(location.state.id)

      if (updatedContact && !updatedContact.name) {
        updateContact(updatedContact.id, { name: initialName.current })
      }
    }
  }, [])

  if (!contact) return null

  const selectEmoji = (avatar: string) => {
    updateContact(contact.id, { avatar })
    setEmojisPickerVisible(false)
  }

  return (
    <div className="mb-3 flex flex-col">
      <div className="mb-6 flex flex-col">
        <Touchable onClick={() => setEmojisPickerVisible(true)} style={{ margin: 'auto' }}>
          <Avatar size={60} emoji={contact.avatar} ring />
        </Touchable>

        <Field className="my-6">
          <Input
            value={contact.name}
            onChange={(e) => updateContact(contact.id, { name: e.target.value })}
            aria-label={contact.name}
          />
        </Field>

        {contact.addresses.length > 0 && (
          <Heading level={5} className="mb-2 px-1">
            {t('contacts.addresses')}
          </Heading>
        )}

        {contact.addresses.map((a) => (
          <Address
            key={a.address}
            chain={a.chain}
            title={t(`chains.long.${a.chain}`)}
            address={a.address}
            navigateOnCopy={false}
            actions={[
              {
                icon: MdDelete,
                onClick: () =>
                  updateContact(contact.id, {
                    addresses: contact.addresses.filter((aa) => aa.address !== a.address),
                  }),
              },
            ]}
          />
        ))}

        <Button
          className="mt-1"
          fullWidth
          variant="secondary"
          onClick={() => setAddressInputVisible(true)}
        >
          {t('contacts.addAddress')}
        </Button>
      </div>

      {contact.addresses.length > 0 && (
        <UserActivity filter={{ users: contact.addresses.map((a) => a.address) }} nested />
      )}

      <Sheet open={emojisPickerVisible} onClose={() => setEmojisPickerVisible(false)}>
        <EmojiPicker
          open={emojisPickerVisible}
          width="100%"
          height={380}
          theme={Theme.DARK}
          style={EMOJI_PICKER_STYLE}
          previewConfig={{ showPreview: false }}
          onEmojiClick={(emoji) => selectEmoji(emoji.emoji)}
          skinTonesDisabled
        />
      </Sheet>

      <BottomSheet
        visible={addressInputVisible}
        onBackdropClick={() => setAddressInputVisible(false)}
      >
        <AddressInput
          onAdd={(address) => {
            updateContact(contact.id, {
              addresses: [...contact.addresses, { address, chain: addressToChain(address)! }],
            })
            setAddressInputVisible(false)
          }}
        />
      </BottomSheet>
    </div>
  )
}

export default ContactDetails
